import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/ui/ToastProvider';
import { NotificationsProvider } from './hooks/useNotifications';
import { CreatorHomeProvider } from './hooks/useCreatorHome';
import LoginPage from './pages/LoginPage';
import CommandCentre from './pages/CommandCentre';
import './styles/App.css';

const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const OutreachAgentPage = lazy(() => import('./pages/OutreachAgentPage'));
const OnboardingAgentPage = lazy(() => import('./pages/OnboardingAgentPage'));
const PostingAgentPage = lazy(() => import('./pages/PostingAgentPage'));
const OnboardingPortalPage = lazy(() => import('./pages/OnboardingPortalPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const ContentUploadAgentPage = lazy(() => import('./pages/ContentUploadAgentPage'));
const CreatorsPage = lazy(() => import('./pages/CreatorsPage'));
const CreatorDetailPage = lazy(() => import('./pages/CreatorDetailPage'));
const CreatorFormPage = lazy(() => import('./pages/CreatorFormPage'));
const InviteCreatorsPage = lazy(() => import('./pages/InviteCreatorsPage'));
const CreatorRegisterPage = lazy(() => import('./pages/CreatorRegisterPage'));
// Public by necessity: whoever opens it cannot sign in, which is the point.
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const CreatorDashboardPage = lazy(() => import('./pages/CreatorDashboardPage'));
const ChatterDashboardPage = lazy(() => import('./pages/ChatterDashboardPage'));
const MarketerDashboardPage = lazy(() => import('./pages/MarketerDashboardPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const SalaryPage = lazy(() => import('./pages/SalaryPage'));
const SalesFeedPage = lazy(() => import('./pages/SalesFeedPage'));
const TaskDetailPage = lazy(() => import('./pages/TaskDetailPage'));
const TaskFormPage = lazy(() => import('./pages/TaskFormPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const TeamMemberDetailPage = lazy(() => import('./pages/TeamMemberDetailPage'));
const TeamFormPage = lazy(() => import('./pages/TeamFormPage'));
const RevenuePage = lazy(() => import('./pages/RevenuePage'));
const CreatorRevenuePage = lazy(() => import('./pages/CreatorRevenuePage'));
const PayrollPage = lazy(() => import('./pages/PayrollPage'));
const PayoutsPage = lazy(() => import('./pages/PayoutsPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const ChatterLedgerPage = lazy(() => import('./pages/ChatterLedgerPage'));
const ChatterEarningsPage = lazy(() => import('./pages/ChatterEarningsPage'));
const ShiftsPage = lazy(() => import('./pages/ShiftsPage'));
const AccountsLibraryPage = lazy(() => import('./pages/AccountsLibraryPage'));
const PostingLibraryPage = lazy(() => import('./pages/PostingLibraryPage'));
const MarketingAnalyticsPage = lazy(() => import('./pages/MarketingAnalyticsPage'));
const TrackingLinksPage = lazy(() => import('./pages/TrackingLinksPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LegalManagePage = lazy(() => import('./pages/LegalManagePage'));
const RemoteControlPage = lazy(() => import('./pages/RemoteControlPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const ContentVaultPage = lazy(() => import('./pages/ContentVaultPage'));
const CreatorContentPage = lazy(() => import('./pages/CreatorContentPage'));
const CreatorTasksPage = lazy(() => import('./pages/CreatorTasksPage'));
const CreatorContentPlanPage = lazy(() => import('./pages/CreatorContentPlanPage'));
const ContentPlanPage = lazy(() => import('./pages/ContentPlanPage'));
const CreatorEarningsPage = lazy(() => import('./pages/CreatorEarningsPage'));
const VideoStudioPage = lazy(() => import('./pages/VideoStudioPage'));
const SopLibraryPage = lazy(() => import('./pages/SopLibraryPage'));
const AcademyPage = lazy(() => import('./pages/AcademyPage'));
const AcademyCoursePage = lazy(() => import('./pages/AcademyCoursePage'));
const AcademyLessonPage = lazy(() => import('./pages/AcademyLessonPage'));
const AcademyQuizPage = lazy(() => import('./pages/AcademyQuizPage'));
const AcademyManagePage = lazy(() => import('./pages/AcademyManagePage'));
const AcademyBuilderPage = lazy(() => import('./pages/AcademyBuilderPage'));
const AcademyProgressPage = lazy(() => import('./pages/AcademyProgressPage'));
const AcademyUserProgressPage = lazy(() => import('./pages/AcademyUserProgressPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));

// Same landing route for every authenticated role, dispatched by what that role
// is allowed to see: the agency metrics dashboard (admin/manager), a chatter's
// own sales/shifts/commission view, a marketer's own subs/commission/balance
// view, or a creator's own. Nobody without view_all_earnings lands on the admin
// one — see AdminDashboardPage's canSeeRevenue for what it hides even then.
// The landing screen. Staff get the command centre; a creator gets her own
// home, which is already the right first screen for her and always was.
// `/` and `/dashboard` must land on the same page for the same role, or a
// chatter arriving at the root gets the agency command centre — which the API
// then refuses, so it renders as a wall of zeros rather than as their own work.
function HomeRouter() {
  return <DashboardRouter />;
}

// `/` and `/dashboard` are the same destination on purpose.
//
// They were two different pages: `/` rendered the redesigned command centre and
// `/dashboard` the old admin dashboard, both claiming currentPage="dashboard",
// so the sidebar's Dashboard link looked right and landed you on the previous
// generation of the UI. One dashboard per role, reachable from either slug.
function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === 'creator') return <CreatorDashboardPage />;
  if (user?.role === 'chatter') return <ChatterDashboardPage />;
  if (user?.role === 'marketer') return <MarketerDashboardPage />;
  return <CommandCentre />;
}

// Routes a role must not reach even by typing the URL — creators kept out of
// staff tooling, marketers out of the money and roster rooms.
//
// Taking a page off the navigation is not access control — the route stays
// live for anyone who has the link, and the page renders with an empty sidebar
// (getModuleForPage finds no room that claims it for that role) which reads as
// broken rather than as forbidden. The API refuses these too — see
// denyRole('creator') in routes/payroll.js and the manage_users gate on
// routes/settings.js — this is the front door saying the same thing.
function RoleGuard({ deny = [], to = '/', children }) {
  const { user } = useAuth();
  if (user && deny.includes(user.role)) return <Navigate to={to} replace />;
  return children;
}

// /content is one route and two products. Staff get the vault — every creator's
// library, with the folder tree, bulk actions and versioning that administering
// it needs. A creator gets her content plan: the week's ideas are what she opens
// Content for, and her library is one tap away at /content/library. Same
// dispatch idea as DashboardRouter above, for the same reason.
function ContentRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <CreatorContentPlanPage /> : <ContentVaultPage />;
}

// The creator's own library, moved off /content when the plan took that slot.
// Staff reaching it are sent to the vault, which is the same thing done properly
// for someone administering every creator at once.
function ContentLibraryRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <CreatorContentPage /> : <Navigate to="/content" replace />;
}

// The planner's side: pick a creator, build their week. Creators never see this
// one — theirs is /content, which is their own plan and nobody else's.
function ContentPlanRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <Navigate to="/content" replace /> : <ContentPlanPage />;
}

// Same idea for tasks. Staff get the table they manage everyone's work from;
// a creator gets their own work as an action centre. The creator page serves
// both /tasks and /tasks/:id, opening the task's sheet over the list rather
// than navigating to a detail page, so deep links cost no back-press.
function TasksRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <CreatorTasksPage /> : <TasksPage />;
}

function TaskDetailRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <CreatorTasksPage /> : <TaskDetailPage />;
}

// /revenue/creators/:id is a staff drill-down into one creator's numbers, and
// also the route a creator's own "My Earnings" points at (with id = 'me').
// Those are two different products at one URL, so they get two pages.
function CreatorRevenueRouter() {
  const { user } = useAuth();
  return user?.role === 'creator' ? <CreatorEarningsPage /> : <CreatorRevenuePage />;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Suspense fallback={<div className="loading">Loading...</div>}>
      <Routes>
        {/* Creator onboarding portal — public by design: the creator has no
            account yet, so the token in the URL is the only credential. Must sit
            outside the isAuthenticated guard. */}
        <Route path="/onboarding/:token" element={<OnboardingPortalPage />} />

        {/* A shared vault folder or file. Public for the same reason: the person
            opening it has no account here and is not getting one, so the token
            in the URL is the whole credential. Must sit outside the
            isAuthenticated guard, and renders without MainLayout — see
            pages/SharePage.jsx. */}
        <Route path="/s/:token" element={<SharePage />} />

        {/* Hub-and-spoke: the launcher home replaces the flat dashboard landing. */}
        <Route path="/" element={isAuthenticated ? <HomeRouter /> : <LoginPage />} />
        <Route path="/register" element={!isAuthenticated ? <CreatorRegisterPage /> : <Navigate to="/" />} />
        {/* Reachable signed in or out — the link is the credential, and being
            signed in on one device is no reason to refuse a reset. */}
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={isAuthenticated ? <DashboardRouter /> : <Navigate to="/" />} />
        <Route path="/creator-dashboard" element={isAuthenticated ? <CreatorDashboardPage /> : <Navigate to="/" />} />
        <Route path="/invites" element={isAuthenticated ? <RoleGuard deny={['marketer']}><InviteCreatorsPage /></RoleGuard> : <Navigate to="/" />} />
        {/* The People room is admin/manager work — a marketer has no
            manage_users, so everything past the roster list refuses them, and
            the pages render with an empty sidebar because no room they can see
            claims those nav ids. Same door-and-API pairing as below. */}
        <Route path="/creators" element={isAuthenticated ? <RoleGuard deny={['marketer']}><CreatorsPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/creators/new" element={isAuthenticated ? <RoleGuard deny={['marketer']}><CreatorFormPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/creators/:id" element={isAuthenticated ? <RoleGuard deny={['marketer']}><CreatorDetailPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/creators/:id/edit" element={isAuthenticated ? <RoleGuard deny={['marketer']}><CreatorFormPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/tasks" element={isAuthenticated ? <TasksRouter /> : <Navigate to="/" />} />
        {/* Creating and editing work is assign_tasks-gated on the API — a
            creator reaching these forms could only ever get a 403 on save. */}
        <Route
          path="/tasks/new"
          element={isAuthenticated
            ? <RoleGuard deny={['creator']} to="/tasks"><TaskFormPage /></RoleGuard>
            : <Navigate to="/" />}
        />
        <Route path="/tasks/:id" element={isAuthenticated ? <TaskDetailRouter /> : <Navigate to="/" />} />
        <Route
          path="/tasks/:id/edit"
          element={isAuthenticated
            ? <RoleGuard deny={['creator']} to="/tasks"><TaskFormPage /></RoleGuard>
            : <Navigate to="/" />}
        />
        <Route path="/team" element={isAuthenticated ? <RoleGuard deny={['marketer']}><TeamPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/team/new" element={isAuthenticated ? <RoleGuard deny={['marketer']}><TeamFormPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/team/:id" element={isAuthenticated ? <RoleGuard deny={['marketer']}><TeamMemberDetailPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/team/:id/edit" element={isAuthenticated ? <RoleGuard deny={['marketer']}><TeamFormPage /></RoleGuard> : <Navigate to="/" />} />
        {/* Finance is not a marketer's room: they have neither view_all_earnings
            nor view_own_earnings, so /api/revenue answers 403, and they are not
            in the earnings ledger the payroll pages are built on. */}
        <Route path="/revenue" element={isAuthenticated ? <RoleGuard deny={['marketer']}><RevenuePage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/revenue/creators/:id" element={isAuthenticated ? <RoleGuard deny={['marketer']}><CreatorRevenueRouter /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/payroll" element={isAuthenticated ? <RoleGuard deny={['marketer']}><PayrollPage /></RoleGuard> : <Navigate to="/" />} />
        {/* The feed scopes itself: agency-wide for view_all_earnings, a
            creator's own sales for a creator, and the assigned creators only
            for a marketer. So the page is open to all three. */}
        <Route path="/sales" element={isAuthenticated ? <SalesFeedPage /> : <Navigate to="/" />} />
        <Route path="/salary" element={isAuthenticated ? <RoleGuard deny={['marketer', 'chatter', 'creator']}><SalaryPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/payouts" element={isAuthenticated ? <RoleGuard deny={['marketer']}><PayoutsPage /></RoleGuard> : <Navigate to="/" />} />
        {/* The payout wallet belongs to people paid through the withdrawal
            queue. Creators are paid on their revenue split and are not in it. */}
        <Route
          path="/payroll/wallet"
          element={isAuthenticated
            ? <RoleGuard deny={['creator']}><WalletPage /></RoleGuard>
            : <Navigate to="/" />}
        />
        {/* Someone else's ledger is a manage_users view, so it stays out of a
            marketer's reach; their own earnings page does not. */}
        <Route path="/payroll/balances/:userId" element={isAuthenticated ? <RoleGuard deny={['marketer']}><ChatterLedgerPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/payroll/my-earnings" element={isAuthenticated ? <ChatterEarningsPage /> : <Navigate to="/" />} />
        <Route path="/shifts" element={isAuthenticated ? <ShiftsPage /> : <Navigate to="/" />} />
        <Route path="/accounts-library" element={isAuthenticated ? <AccountsLibraryPage /> : <Navigate to="/" />} />
        <Route path="/posting-library" element={isAuthenticated ? <PostingLibraryPage /> : <Navigate to="/" />} />
        <Route path="/marketing-analytics" element={isAuthenticated ? <RoleGuard deny={['creator']}><MarketingAnalyticsPage /></RoleGuard> : <Navigate to="/" />} />
        {/* Marketers and staff only. A creator or chatter has no nav item claiming
            this page, so without the guard they would land on it with an empty
            sidebar — which reads as broken rather than as forbidden. */}
        {/* A room and the places inside it — what the sidebar used to list. */}
        <Route path="/rooms/:moduleId" element={isAuthenticated ? <RoomPage /> : <Navigate to="/" />} />
        <Route path="/tracking-links" element={isAuthenticated ? <RoleGuard deny={['creator', 'chatter']}><TrackingLinksPage /></RoleGuard> : <Navigate to="/" />} />
        {/* Two tabs, not one page — see LegalPage's own note. Every role gets
            Contract; the API scopes the read, not this route. */}
        <Route path="/legal" element={<Navigate to="/legal/contract" replace />} />
        <Route path="/legal/contract" element={isAuthenticated ? <LegalPage tab="contract" /> : <Navigate to="/" />} />
        {/* Invoices only exist for a creator (an invoice is the agency billing
            them, which has no equivalent for staff), and only a creator's nav
            offers this tab — so the route has to refuse everyone else too, or
            a currentPage no nav item claims for their role renders an empty
            sidebar. Sent back to Contract rather than off the room entirely. */}
        <Route
          path="/legal/invoices"
          element={isAuthenticated
            ? <RoleGuard deny={['admin', 'manager', 'marketer', 'chatter']} to="/legal/contract"><LegalPage tab="invoices" /></RoleGuard>
            : <Navigate to="/" />}
        />
        {/* Attaching a contract or an invoice is manage_users work; the route
            denies everyone who cannot possibly hold that permission the same
            way /team and /invites do, and the API denies it again either way. */}
        <Route path="/legal/manage" element={isAuthenticated ? <RoleGuard deny={['creator', 'chatter', 'marketer']}><LegalManagePage /></RoleGuard> : <Navigate to="/" />} />
        {/* Staff only. The page itself shows a person only the phones assigned
            to them, and every connect is re-authorised server-side. */}
        <Route path="/remote-control" element={isAuthenticated ? <RoleGuard deny={['creator']}><RemoteControlPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/content" element={isAuthenticated ? <ContentRouter /> : <Navigate to="/" />} />
        <Route path="/content/plan" element={isAuthenticated ? <ContentPlanRouter /> : <Navigate to="/" />} />
        <Route path="/content/library" element={isAuthenticated ? <ContentLibraryRouter /> : <Navigate to="/" />} />
        <Route path="/video-studio" element={isAuthenticated ? <VideoStudioPage /> : <Navigate to="/" />} />
        <Route path="/video-spoofer" element={isAuthenticated ? <VideoStudioPage initialTab="spoofer" /> : <Navigate to="/" />} />
        {/* Legacy paths kept alive so old links/bookmarks still land — they open
            the merged Video Studio on the matching tab. */}
        <Route path="/video-variants" element={isAuthenticated ? <VideoStudioPage initialTab="posting" /> : <Navigate to="/" />} />
        <Route path="/video-tools" element={isAuthenticated ? <VideoStudioPage initialTab="spoofer" /> : <Navigate to="/" />} />
        <Route path="/sops" element={isAuthenticated ? <SopLibraryPage /> : <Navigate to="/" />} />
        {/* Training Academy — lives alongside the SOP Library in the same room.
            Specific routes precede the :id ones so /manage and /progress can't be
            swallowed by /courses/:id-style patterns. */}
        <Route path="/academy" element={isAuthenticated ? <AcademyPage /> : <Navigate to="/" />} />
        <Route path="/academy/manage" element={isAuthenticated ? <AcademyManagePage /> : <Navigate to="/" />} />
        <Route path="/academy/manage/:id" element={isAuthenticated ? <AcademyBuilderPage /> : <Navigate to="/" />} />
        <Route path="/academy/progress" element={isAuthenticated ? <AcademyProgressPage /> : <Navigate to="/" />} />
        <Route path="/academy/progress/:userId/:courseId" element={isAuthenticated ? <AcademyUserProgressPage /> : <Navigate to="/" />} />
        <Route path="/academy/courses/:id" element={isAuthenticated ? <AcademyCoursePage /> : <Navigate to="/" />} />
        <Route path="/academy/lessons/:id" element={isAuthenticated ? <AcademyLessonPage /> : <Navigate to="/" />} />
        <Route path="/academy/quizzes/:id" element={isAuthenticated ? <AcademyQuizPage /> : <Navigate to="/" />} />
        <Route path="/reports" element={isAuthenticated ? <RoleGuard deny={['marketer']}><ReportsPage /></RoleGuard> : <Navigate to="/" />} />
        <Route path="/agents" element={isAuthenticated ? <AgentsPage /> : <Navigate to="/" />} />
        <Route path="/agents/outreach" element={isAuthenticated ? <OutreachAgentPage /> : <Navigate to="/" />} />
        {/* The upload agent is the staff tool for filing content on a
            creator's behalf. Creators upload from their own library, where the
            flow already knows the answers to its questions. */}
        <Route
          path="/agents/content-upload"
          element={isAuthenticated
            ? <RoleGuard deny={['creator']} to="/content"><ContentUploadAgentPage /></RoleGuard>
            : <Navigate to="/" />}
        />
        <Route path="/agents/posting" element={isAuthenticated ? <PostingAgentPage /> : <Navigate to="/" />} />
        <Route path="/agents/posting/:id" element={isAuthenticated ? <PostingAgentPage /> : <Navigate to="/" />} />
        <Route path="/agents/onboarding" element={isAuthenticated ? <OnboardingAgentPage /> : <Navigate to="/" />} />
        <Route path="/agents/onboarding/:id" element={isAuthenticated ? <OnboardingAgentPage /> : <Navigate to="/" />} />
        {/* App configuration. The account menu offers it to every role, and for
            a creator it rendered as a chrome-less page of 403s. */}
        <Route
          path="/settings"
          element={isAuthenticated
            ? <RoleGuard deny={['creator']}><SettingsPage /></RoleGuard>
            : <Navigate to="/" />}
        />
        <Route path="/notifications" element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/" />} />
        <Route path="/messages" element={isAuthenticated ? <MessagesPage /> : <Navigate to="/" />} />
        <Route path="/announcements" element={isAuthenticated ? <AnnouncementsPage /> : <Navigate to="/" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            {/* Above the routes, not inside MainLayout: pages call
                useNotifications() in their own body, which is outside any
                provider their own layout renders. */}
            <NotificationsProvider>
              {/* One shared read of a creator's outstanding actions, for the
                  same reason: the dashboard, Operations and the Rooms grid all
                  ask "what does WTSN need from me?" and must agree. Inert for
                  every other role — staff never fetch it. */}
              <CreatorHomeProvider>
                <AppRoutes />
              </CreatorHomeProvider>
            </NotificationsProvider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
